import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getRankInfo } from '../utils/rankUtils';
import { getRankFrameStyle, hasVerifiedBadge } from '../utils/rankFeatures';
import './Profile.css';

interface UserProfileProps {
  userId: string;
  onNavigate: (page: string) => void;
  onClose: () => void;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  rollNo: string;
  department: string;
  points: number;
  rank: string;
  avatar_url?: string;
  bio?: string;
  total_activities: number;
  streak: number;
}

const UserProfile: React.FC<UserProfileProps> = ({ userId, onNavigate, onClose }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    fetchUserData();
    fetchRecentActivities();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUserData(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentActivities(data || []);
    } catch (error) {
    }
  };

  if (loading) {
    return <div className="user-profile-modal loading">Loading...</div>;
  }

  if (!userData) {
    return <div className="user-profile-modal">User not found</div>;
  }

  const rankInfo = getRankInfo(userData.rank);

  return (
    <div className="user-profile-modal-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>User Profile</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-content">
          <div className="profile-section">
            <div className="profile-card">
              <div className="profile-avatar">
                <div className="avatar-frame" style={getRankFrameStyle(userData.rank)}>
                  {userData.avatar_url ? (
                    <img src={userData.avatar_url} alt={userData.name} className="avatar-image" />
                  ) : (
                    <div className="avatar-large">{userData.name.charAt(0).toUpperCase()}</div>
                  )}
                </div>
                <div className="rank-badge-large" style={{ background: rankInfo.color }}>
                  <span>{rankInfo.icon}</span>
                  <span>{rankInfo.name}</span>
                  {hasVerifiedBadge(userData.rank) && <span className="verified-badge">✓</span>}
                </div>
              </div>

              <div className="profile-info">
                <h2>{userData.name}</h2>
                <p className="roll-no">{userData.rollNo}</p>
                <p className="department">{userData.department}</p>
                {userData.bio && <p className="bio">{userData.bio}</p>}
              </div>
            </div>
          </div>

          <div className="stats-section">
            <div className="stat-item">
              <div className="stat-value">{userData.points}</div>
              <div className="stat-label">Points</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{userData.total_activities || 0}</div>
              <div className="stat-label">Activities</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{userData.streak || 0}</div>
              <div className="stat-label">Day Streak</div>
            </div>
          </div>

          <div className="recent-activities-section">
            <h3>Recent Activities</h3>
            {recentActivities.length > 0 ? (
              <ul className="activity-list">
                {recentActivities.map(activity => (
                  <li key={activity.id}>
                    <span>{new Date(activity.created_at).toLocaleDateString()}</span>
                    <span>{activity.points_earned || 0} points</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No recent activities</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;